import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Puzzle,
  Compass,
  Briefcase,
  MessageSquare,
  Battery,
  Heart,
  ShieldCheck,
  Sparkles,
  Plus,
  Minus,
} from "lucide-react";
import SEO from "@/components/SEO";

const BOOKING_URL = "https://sophia-dinwiddie-donald.clientsecure.me/request/clinician";

const HERO_IMG =
  "https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=1600&q=80";

const resonates = [
  "You're exhausted from masking — performing 'normal' at work, in friendships, with family.",
  "Routines, transitions, and decisions feel heavier than people seem to understand.",
  "You were diagnosed (or self-identified) as autistic later in life, and you're still processing what it means.",
  "Sensory overload — sound, light, social density — wrecks more of your week than you'd like to admit.",
  "You can do hard, complex things — but the 'simple' adult tasks (paperwork, errands, replies) pile up and shame you.",
  "You want a therapist who gets it, who won't try to make you less autistic.",
];

const focusAreas = [
  {
    icon: Compass,
    title: "Executive Function & Routines",
    desc: "Task initiation, planning, transitions, and the daily rhythms that prevent collapse — built to fit your brain, not against it.",
  },
  {
    icon: Battery,
    title: "Sensory Regulation & Burnout Recovery",
    desc: "Identify your sensory profile, build a regulation toolkit, and recover from autistic burnout with structure and gentleness.",
  },
  {
    icon: Briefcase,
    title: "Workplace & Career Navigation",
    desc: "Disclosure decisions, accommodation requests, communication scripts, managing meetings and unwritten rules.",
  },
  {
    icon: MessageSquare,
    title: "Communication & Relationships",
    desc: "Scripts and practice for hard conversations, conflict, friendship, dating, and asking for what you need — without scripting away your voice.",
  },
  {
    icon: Heart,
    title: "Late-Diagnosis Processing",
    desc: "Grieving what could've been, rewriting your story, and integrating a new (and old) identity at any age.",
  },
  {
    icon: ShieldCheck,
    title: "Emotional Self-Advocacy",
    desc: "Build the language and confidence to set limits, name what's hard, and protect the energy that fuels everything else.",
  },
];

const principles = [
  {
    title: "Identity-affirming, not masking-driven",
    body: "We don't treat autism as a problem to solve. We treat life as a thing to live well, on autistic terms.",
  },
  {
    title: "Skills > scripts > shame",
    body: "We focus on practical, durable skills — and we never pathologize the parts of you that need a different approach.",
  },
  {
    title: "Pace set by you",
    body: "Some weeks are deep dives. Others are maintenance. Therapy and coaching should respect your bandwidth.",
  },
  {
    title: "Whole-system aware",
    body: "We consider co-occurring ADHD, anxiety, trauma, and chronic illness — because most autistic adults aren't navigating just one thing.",
  },
];

const faqs = [
  {
    q: "Do I need a formal autism diagnosis to work with you?",
    a: "No. Many of our clients are self-identified, late-diagnosed, or in the process of seeking evaluation. We meet you where you are. If a referral for assessment makes sense, we can discuss it.",
  },
  {
    q: "Is this therapy or coaching?",
    a: "Both, depending on what you need. Sessions can blend therapeutic work (processing emotions, trauma, identity) with practical skills coaching (routines, scripts, work strategies). We tailor each session to what's most useful that week.",
  },
  {
    q: "Do you use ABA?",
    a: "No. Our approach is neurodivergent-affirming. We do not use Applied Behavior Analysis or any framework that treats autistic traits as problems to extinguish. We draw on CBT-adapted work, acceptance-based approaches, sensory integration principles, and lived neurodivergent wisdom.",
  },
  {
    q: "Can we meet virtually?",
    a: "Yes — virtual sessions are available across all of Texas, which makes care accessible without the executive-function tax of getting to an office.",
  },
  {
    q: "What about co-occurring ADHD or anxiety?",
    a: "Very common, and very welcome. We routinely support clients with AuDHD and anxiety. We'll build a plan that addresses what's loudest while honoring the whole picture.",
  },
  {
    q: "How long does this kind of work take?",
    a: "Some clients use a few months to build a skill or process a diagnosis. Others stay longer for ongoing support. There's no formula — we'll review progress regularly and adjust together.",
  },
];

function Faq({ q, a, idx, open, onToggle }) {
  return (
    <div
      data-testid={`als-faq-${idx}`}
      className="border-b border-sage/30 py-5"
    >
      <button
        onClick={onToggle}
        aria-expanded={open}
        className="w-full flex items-start justify-between gap-6 text-left group"
      >
        <span className="font-display text-xl md:text-2xl text-forest font-medium leading-snug group-hover:text-terracotta transition-colors">
          {q}
        </span>
        <span className="mt-1 h-8 w-8 rounded-full bg-forest/5 text-forest flex items-center justify-center shrink-0 group-hover:bg-terracotta group-hover:text-bone transition-colors">
          {open ? <Minus size={16} strokeWidth={1.6} /> : <Plus size={16} strokeWidth={1.6} />}
        </span>
      </button>
      {open && (
        <p className="mt-4 text-ink/75 leading-relaxed max-w-3xl animate-fade-in">{a}</p>
      )}
    </div>
  );
}

export default function AutismLifeSkills() {
  const [openIdx, setOpenIdx] = useState(0);

  const serviceJsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Life Skills Coaching for Autistic Adults",
    serviceType: "Neurodivergent-affirming therapy and life-skills coaching for autistic adults",
    provider: {
      "@type": "ProfessionalService",
      name: "Seven Pillars of Life Therapy",
      url: "https://www.7pillarsoflife.com",
      areaServed: { "@type": "State", name: "Texas" },
    },
    areaServed: { "@type": "State", name: "Texas" },
    audience: { "@type": "PeopleAudience", suggestedMinAge: 18 },
    description:
      "Neurodivergent-affirming therapy and life-skills coaching for autistic adults in Texas. Executive function, sensory regulation, late-diagnosis processing, workplace navigation, and burnout recovery — without masking.",
  };

  return (
    <div data-testid="autism-life-skills-page">
      <SEO
        title="Life Skills for Autistic Adults in Texas"
        description="Neurodivergent-affirming therapy and life-skills coaching for autistic adults across Texas. Executive function, sensory regulation, late-diagnosis processing, workplace navigation — without masking."
        path="/autism-life-skills"
        image={HERO_IMG}
        jsonLd={serviceJsonLd}
      />

      {/* HERO */}
      <section className="relative min-h-[78vh] flex items-end overflow-hidden">
        <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-forest/90 via-forest/55 to-forest/15" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24 w-full">
          <div className="max-w-3xl animate-fade-up">
            <p className="overline text-terracotta mb-5 inline-flex items-center gap-2">
              <Puzzle size={12} strokeWidth={1.6} /> Autism &amp; Life Skills
            </p>
            <h1 className="font-display text-bone font-semibold text-5xl md:text-7xl leading-[1.04] tracking-tight" style={{ textShadow: "0 2px 24px rgba(0,0,0,0.25)" }}>
              Therapy and life skills, <em className="italic text-terracotta">built for autistic adults.</em>
            </h1>
            <p className="mt-7 text-lg md:text-xl text-bone/90 max-w-2xl leading-relaxed">
              Neurodivergent-affirming care across Texas. We help you live well on your terms — without masking, without shame, without trying to make you less autistic.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <a
                href={BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="als-hero-book-btn"
                className="inline-flex items-center justify-center gap-2 bg-terracotta hover:bg-terracottaDark text-bone rounded-full px-8 py-4 font-medium transition-colors"
              >
                Book a free 15-min call <ArrowRight size={18} strokeWidth={1.6} />
              </a>
              <Link
                to="/contact"
                data-testid="als-hero-contact-btn"
                className="inline-flex items-center justify-center bg-transparent border border-bone/60 text-bone hover:bg-bone hover:text-forest rounded-full px-8 py-4 font-medium transition-colors"
              >
                Send a message
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* DOES THIS RESONATE */}
      <section className="py-24 md:py-32 bg-bone" data-testid="als-resonate-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-14 lg:gap-20 items-start">
          <div className="lg:col-span-5 lg:sticky lg:top-32">
            <p className="overline mb-5">If You're Here…</p>
            <h2 className="font-display text-4xl md:text-5xl text-forest font-medium leading-tight tracking-tight">
              Does this <em className="italic text-terracotta">resonate?</em>
            </h2>
            <p className="mt-6 text-ink/75 leading-relaxed">
              You don't have to qualify, perform, or justify your way into help. If any of these land — we'd love to talk.
            </p>
          </div>
          <ul className="lg:col-span-7 space-y-5">
            {resonates.map((r, i) => (
              <li
                key={i}
                className="flex gap-5 items-start p-6 bg-surface rounded-2xl border border-sage/25 hover:border-terracotta/40 transition-all duration-300"
              >
                <span className="font-display text-2xl text-terracotta font-semibold leading-none mt-1">{String(i + 1).padStart(2, "0")}</span>
                <p className="text-lg text-ink/85 leading-relaxed">{r}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* FOCUS AREAS */}
      <section className="py-24 md:py-32 bg-surface" data-testid="als-focus-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-2xl mb-14">
            <p className="overline mb-5">What We Work On</p>
            <h2 className="font-display text-4xl md:text-5xl text-forest font-medium leading-tight tracking-tight">
              Practical skills, <em className="italic text-terracotta">honest support.</em>
            </h2>
            <p className="mt-5 text-ink/70 leading-relaxed">
              Sessions blend therapy and coaching. Some weeks are emotional. Some are tactical. All are paced to your bandwidth.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {focusAreas.map((f) => {
              const Icon = f.icon;
              return (
                <article
                  key={f.title}
                  data-testid={`als-focus-${f.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                  className="bg-bone border border-sage/25 rounded-2xl p-8 hover:-translate-y-1 hover:border-terracotta/40 hover:shadow-xl transition-all duration-300"
                >
                  <div className="h-12 w-12 rounded-full bg-forest/5 flex items-center justify-center text-forest mb-5">
                    <Icon size={22} strokeWidth={1.5} />
                  </div>
                  <h3 className="font-display text-2xl text-forest font-medium mb-3 leading-snug">{f.title}</h3>
                  <p className="text-ink/70 leading-relaxed text-[15px]">{f.desc}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* APPROACH / PRINCIPLES */}
      <section className="py-24 md:py-32 bg-forest text-bone relative overflow-hidden" data-testid="als-approach-section">
        <div className="absolute inset-0 opacity-10 pointer-events-none" style={{
          backgroundImage: "radial-gradient(#FAF7FC 1px, transparent 1px)",
          backgroundSize: "32px 32px",
        }} />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-2xl mb-14">
            <p className="overline text-terracotta mb-5">Our Approach</p>
            <h2 className="font-display text-4xl md:text-6xl font-medium leading-[1.05] tracking-tight">
              Neurodivergent-affirming. <em className="italic text-terracotta">Always.</em>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-bone/15 rounded-2xl overflow-hidden">
            {principles.map((p, i) => (
              <div key={p.title} className="bg-forest p-8 md:p-10 hover:bg-[#5D4B85] transition-colors duration-300">
                <p className="font-display text-3xl text-terracotta font-semibold leading-none mb-5">{String(i + 1).padStart(2, "0")}</p>
                <h3 className="font-display text-2xl md:text-3xl text-bone font-medium mb-3 leading-snug">{p.title}</h3>
                <p className="text-bone/75 leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-24 md:py-32 bg-bone" data-testid="als-how-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-2xl mb-14">
            <p className="overline mb-5">How It Works</p>
            <h2 className="font-display text-4xl md:text-5xl text-forest font-medium leading-tight tracking-tight">
              Three steps <em className="italic text-terracotta">to begin.</em>
            </h2>
          </div>
          <ol className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { n: "01", t: "Free 15-min consultation", b: "We'll talk briefly to understand what you're navigating and whether we're the right fit. No pressure, no pitch." },
              { n: "02", t: "Intake & goal-setting", b: "First full session is unhurried. We co-create a roadmap of what you'd like to work on and at what pace." },
              { n: "03", t: "Weekly or bi-weekly sessions", b: "Virtual sessions across Texas. Cadence is yours to set — and adjust whenever life shifts." },
            ].map((s) => (
              <li key={s.n} className="bg-surface border border-sage/25 rounded-2xl p-8">
                <p className="font-display text-5xl text-terracotta font-semibold leading-none mb-6">{s.n}</p>
                <h3 className="font-display text-2xl text-forest font-medium mb-3 leading-snug">{s.t}</h3>
                <p className="text-ink/70 leading-relaxed">{s.b}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24 md:py-32 bg-surface" data-testid="als-faq-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <p className="overline mb-5">Questions, Honestly Answered</p>
          <h2 className="font-display text-4xl md:text-5xl text-forest font-medium leading-tight tracking-tight mb-12">
            FAQ
          </h2>
          <div>
            {faqs.map((f, i) => (
              <Faq
                key={i}
                idx={i}
                q={f.q}
                a={f.a}
                open={openIdx === i}
                onToggle={() => setOpenIdx(openIdx === i ? -1 : i)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* CLOSING CTA */}
      <section className="py-24 md:py-32 bg-bone" data-testid="als-cta-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <Sparkles size={28} className="text-terracotta mx-auto mb-6" strokeWidth={1.4} />
          <p className="overline mb-5">When You're Ready</p>
          <h2 className="font-display text-4xl md:text-6xl text-forest font-medium leading-tight tracking-tight">
            A life built around <em className="italic text-terracotta">how you actually work.</em>
          </h2>
          <p className="mt-6 text-lg text-ink/70 max-w-2xl mx-auto leading-relaxed">
            Schedule a free 15-minute consultation. Bring your questions, your skepticism, and the parts of yourself you usually mask. We'll meet all of you.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="als-final-book-btn"
              className="inline-flex items-center justify-center bg-forest hover:bg-[#5D4B85] text-bone rounded-full px-8 py-4 font-medium transition-colors"
            >
              Book a free consultation
            </a>
            <Link
              to="/contact"
              data-testid="als-final-contact-btn"
              className="inline-flex items-center justify-center bg-transparent border border-forest text-forest hover:bg-forest hover:text-bone rounded-full px-8 py-4 font-medium transition-colors"
            >
              Send a message instead
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
